import ky from "ky";
import { useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { hooks } from "~/lib/ky-utils";

import type { SearchNewResponse, SearchNewRequest } from "~/api/search-new";

import { useSite } from "./useSite";
import { useGoogleTagManager } from "./useGoogleTagManager";

export function useSearch({
  limit = 50,
  searchQuery,
}: {
  limit?: number;
  searchQuery: string;
}) {
  const site = useSite();
  const googleTagManager = useGoogleTagManager();

  const trimmedQuery = searchQuery.trim();

  const lastCompletedQueryRef = useRef("");

  // Clear lastCompletedQueryRef is user has cleared the query.
  useEffect(() => {
    if (!trimmedQuery) lastCompletedQueryRef.current = "";
  }, [trimmedQuery]);

  return useQuery({
    queryKey: ["search", trimmedQuery, limit],
    queryFn: async ({ signal }) => {
      const res = await ky
        .post("/api/search-new", {
          hooks,
          signal,
          headers: { "X-Site-Handle": site.handle },
          json: {
            limit,
            searchQuery: trimmedQuery,
          } satisfies SearchNewRequest,
        })
        .json<SearchNewResponse>();

      googleTagManager.push({
        event: "search",
        properties: {
          search_term: trimmedQuery,
        },
      });

      lastCompletedQueryRef.current = trimmedQuery;

      return res;
    },
    enabled: !!trimmedQuery, // Do not run with an empty query
    staleTime: 1000 * 60, // Cache for 1 minute
    /**
     * Keep previous query result visible while the new one loads.
     *
     * We only use the previous query data if both the current and the last
     * queries are actually set.
     *
     * This avoids a situation where the user clears the query (trimmedQuery
     * becomes empty), then begins typing, and it shows the results from a
     * query they previously made, which is confusing.
     */
    placeholderData: (previousData) => {
      const shouldShowPreviousData =
        !!trimmedQuery && !!lastCompletedQueryRef.current;

      return shouldShowPreviousData ? previousData : undefined;
    },
  });
}
